function _extends() { return _extends = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends.apply(null, arguments); }
/*
 * Market Intelligence on the App UI standard kit (PlunkDocs specs/APP_UI_STANDARD_2026-09-29.md).
 * The report-style sections in sections.jsx stay for the AI drawer and the R&S segment table;
 * everything the page shows directly is built here on Card / Ribbon / Delta.
 *
 * Honesty rules carried over from Website Analytics:
 *  - a delta needs a real previous value (previous = 0 → "no comparison", never "+∞%")
 *  - geo-targeted web metrics are the footprint subset, verified by IP location
 *  - products that share one page share that page's web numbers, and the table says so
 */
import React from 'react';
import { withRouter } from 'react-router';
import { Card, Empty, Delta, Search, Filters, Pending } from '../../components/analytics-kit';
import { hasTenantFeature } from '../../functions/tenant-features';
import { bucketCampaignsByType, totalsForRows } from './aggregations';
import { deriveAnalysis } from './analysis';
import { NON_CTR_TYPES } from './report-config';
import { resolveTitle, clean } from './title-resolver';
export const fmt = {
  int: n => Number(n) < 0 ? '—' : Number(n || 0).toLocaleString('en-US'),
  dec1: n => Number(n || 0).toLocaleString('en-US', {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1
  }),
  dec2: n => Number(n || 0).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }),
  pct: n => `${Number(n || 0).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  })}%`,
  money0: n => `$${Number(n || 0).toLocaleString('en-US', {
    maximumFractionDigits: 0
  })}`
};

/** Kit-shaped delta: {dir, pct} or {dir, pct, unit:'pp'}; null when there is nothing real to compare against. */
export function dOf(current, previous, opts = {}) {
  if (previous === undefined || previous === null) return null;
  const c = Number(current) || 0,
    p = Number(previous) || 0;
  if (opts.pp) {
    if (!p && !c) return null;
    const d = c - p;
    return {
      dir: d > 0 ? 'up' : d < 0 ? 'down' : 'flat',
      pct: d,
      unit: 'pp'
    };
  }
  if (!p) return null;
  const d = (c - p) / Math.abs(p) * 100;
  return {
    dir: d > 0 ? 'up' : d < 0 ? 'down' : 'flat',
    pct: d
  };
}
export const sumPlaces = (index, key) => Object.values(index || {}).reduce((s, p) => s + (Number(p[key]) || 0), 0);

// --- Ribbon tiles ------------------------------------------------------------

/** Six tiles for a set of campaign rows + a place/geo aggregate (cluster or one market). */
export function ribbonTiles({
  rows,
  prevRows,
  web,
  prevWeb
}) {
  const t = totalsForRows(rows || []);
  const pt = prevRows ? totalsForRows(prevRows) : null;
  const w = web || {};
  const pw = prevWeb || null;
  const pps = w.sessions > 0 ? w.views / w.sessions : 0;
  return [{
    l: 'Impressions',
    v: fmt.int(t.impressions),
    d: pt && dOf(t.impressions, pt.impressions),
    s: Number(t.videoViews) > 0 ? `${fmt.int(t.videoViews)} video views` : `${fmt.int(t.campaignCount)} campaigns`
  }, {
    l: 'Clicks',
    v: fmt.int(t.clicks),
    d: pt && dOf(t.clicks, pt.clicks),
    s: `${fmt.int(t.campaignCount)} campaigns`
  }, {
    l: 'CTR',
    v: fmt.pct(t.ctr),
    d: pt && dOf(t.ctr, pt.ctr, {
      pp: true
    }),
    s: 'click inventory only'
  }, {
    l: 'Geo-targeted views',
    v: fmt.int(w.views),
    d: pw && dOf(w.views, pw.views),
    s: pps ? `${fmt.dec2(pps)} pages per session` : null
  }, {
    l: 'Sessions',
    v: fmt.int(w.sessions),
    d: pw && dOf(w.sessions, pw.sessions),
    s: Number(w.avgTimeSpan) > 0 ? `${fmt.dec1(w.avgTimeSpan)}s average session` : null
  }, {
    l: 'Users',
    v: fmt.int(w.uniqueUsers),
    d: pw && dOf(w.uniqueUsers, pw.uniqueUsers),
    s: Number(w.conversions) > 0 ? `${fmt.int(w.conversions)} conversions` : 'no conversions in this window'
  }];
}
export const GEO_NOTE = 'Geo-targeted web metrics count only visitors inside the market footprints whose location was verified by IP; VPN, proxy and privacy-relay traffic is excluded.';

// --- Cards -------------------------------------------------------------------

function ViewInCampaigns({
  q
}) {
  if (!hasTenantFeature('campaigns')) return null;
  return /*#__PURE__*/React.createElement("a", {
    href: `/Campaigns${q ? `?q=${encodeURIComponent(q)}` : ''}`
  }, "View in Campaigns \u2192");
}

/** Campaigns bucketed by channel type, with a total row. */
export function ChannelsCard({
  rows,
  loading,
  market = '',
  span = 6,
  title = 'Campaigns by channel'
}) {
  const buckets = bucketCampaignsByType(rows || []);
  const totals = totalsForRows(rows || []);
  const hasNonCtr = buckets.some(b => NON_CTR_TYPES.has(b.type));
  return /*#__PURE__*/React.createElement(Card, {
    title: title,
    hint: rows && rows.length ? `${fmt.int(totals.campaignCount)} campaigns` : null,
    right: /*#__PURE__*/React.createElement(ViewInCampaigns, {
      q: market
    }),
    span: span
  }, loading ? /*#__PURE__*/React.createElement(Pending, null) : !rows || !rows.length ? /*#__PURE__*/React.createElement(Empty, null, "No paid campaigns delivered here in this window. Connect an ad account under Campaigns \u203A Settings, or let Autopilot draft the first flight.") : /*#__PURE__*/React.createElement("div", {
    className: "tblwrap"
  }, /*#__PURE__*/React.createElement("table", {
    className: "territory-table wa-table"
  }, /*#__PURE__*/React.createElement("thead", null, /*#__PURE__*/React.createElement("tr", null, /*#__PURE__*/React.createElement("th", null, "Channel"), /*#__PURE__*/React.createElement("th", {
    className: "n"
  }, "Campaigns"), /*#__PURE__*/React.createElement("th", {
    className: "n"
  }, "Impressions"), /*#__PURE__*/React.createElement("th", {
    className: "n"
  }, "Video views"), /*#__PURE__*/React.createElement("th", {
    className: "n"
  }, "Clicks"), /*#__PURE__*/React.createElement("th", {
    className: "n"
  }, "CTR"))), /*#__PURE__*/React.createElement("tbody", null, buckets.map(b => /*#__PURE__*/React.createElement("tr", {
    key: b.type
  }, /*#__PURE__*/React.createElement("td", null, /*#__PURE__*/React.createElement("b", null, b.type)), /*#__PURE__*/React.createElement("td", {
    className: "n"
  }, fmt.int(b.campaignCount)), /*#__PURE__*/React.createElement("td", {
    className: "n"
  }, fmt.int(b.impressions)), /*#__PURE__*/React.createElement("td", {
    className: "n"
  }, Number(b.videoViews) > 0 ? fmt.int(b.videoViews) : '—'), /*#__PURE__*/React.createElement("td", {
    className: "n"
  }, fmt.int(b.clicks)), /*#__PURE__*/React.createElement("td", {
    className: "n"
  }, b.ctr === null ? '—' : fmt.pct(b.ctr)))), /*#__PURE__*/React.createElement("tr", {
    className: "total"
  }, /*#__PURE__*/React.createElement("td", null, /*#__PURE__*/React.createElement("b", null, "Total")), /*#__PURE__*/React.createElement("td", {
    className: "n"
  }, /*#__PURE__*/React.createElement("b", null, fmt.int(totals.campaignCount))), /*#__PURE__*/React.createElement("td", {
    className: "n"
  }, /*#__PURE__*/React.createElement("b", null, fmt.int(totals.impressions))), /*#__PURE__*/React.createElement("td", {
    className: "n"
  }, /*#__PURE__*/React.createElement("b", null, Number(totals.videoViews) > 0 ? fmt.int(totals.videoViews) : '—')), /*#__PURE__*/React.createElement("td", {
    className: "n"
  }, /*#__PURE__*/React.createElement("b", null, fmt.int(totals.clicks))), /*#__PURE__*/React.createElement("td", {
    className: "n"
  }, /*#__PURE__*/React.createElement("b", null, fmt.pct(totals.ctr))))))), hasNonCtr ? /*#__PURE__*/React.createElement("p", {
    className: "wa-foot"
  }, "CTR is the mean of per-campaign daily CTRs and leaves out ", Array.from(NON_CTR_TYPES).join(', '), ": video buys have no clickable inventory.") : null);
}

/** Market leaderboard for the cluster view: one row per market, click to open it. */
export function MarketsCard({
  markets,
  placeIndex,
  prevPlaceIndex,
  marketData,
  prevReady,
  onOpen,
  span = 4
}) {
  const rows = (markets || []).map(m => {
    const p = (placeIndex || {})[m] || {};
    const pp = prevPlaceIndex ? (prevPlaceIndex || {})[m] || {} : null;
    const t = totalsForRows(((marketData || {})[m] || {}).rows || []);
    return {
      m,
      views: Number(p.views) || 0,
      sessions: Number(p.sessions) || 0,
      conversions: Number(p.conversions) || 0,
      impressions: t.impressions,
      d: pp ? dOf(p.views, pp.views) : null
    };
  }).sort((a, b) => b.views - a.views);
  return /*#__PURE__*/React.createElement(Card, {
    title: "Markets",
    hint: "geo-targeted views, this range",
    span: span
  }, !rows.length ? /*#__PURE__*/React.createElement(Empty, null, "No markets yet. Add one under Manage Data \u203A Locations and it becomes trackable here.") : /*#__PURE__*/React.createElement("div", {
    className: "tblwrap"
  }, /*#__PURE__*/React.createElement("table", {
    className: "territory-table wa-table"
  }, /*#__PURE__*/React.createElement("thead", null, /*#__PURE__*/React.createElement("tr", null, /*#__PURE__*/React.createElement("th", null, "Market"), /*#__PURE__*/React.createElement("th", {
    className: "n"
  }, "Views"), /*#__PURE__*/React.createElement("th", {
    className: "n"
  }, "Sessions"), /*#__PURE__*/React.createElement("th", {
    className: "n"
  }, "Impr."), /*#__PURE__*/React.createElement("th", {
    className: "n"
  }, "\u0394 views"))), /*#__PURE__*/React.createElement("tbody", null, rows.map(r => /*#__PURE__*/React.createElement("tr", {
    key: r.m,
    className: "clickable",
    style: {
      cursor: 'pointer'
    },
    onClick: () => onOpen && onOpen(r.m),
    title: `Open ${r.m}`
  }, /*#__PURE__*/React.createElement("td", null, /*#__PURE__*/React.createElement("span", {
    className: "name"
  }, r.m)), /*#__PURE__*/React.createElement("td", {
    className: "n"
  }, fmt.int(r.views)), /*#__PURE__*/React.createElement("td", {
    className: "n"
  }, fmt.int(r.sessions)), /*#__PURE__*/React.createElement("td", {
    className: "n"
  }, r.impressions ? fmt.int(r.impressions) : '—'), /*#__PURE__*/React.createElement("td", {
    className: "n"
  }, !prevReady ? /*#__PURE__*/React.createElement("span", {
    className: "wa-d flat"
  }, "\u2026") : r.d ? /*#__PURE__*/React.createElement(Delta, {
    d: r.d
  }) : /*#__PURE__*/React.createElement("span", {
    className: "dim"
  }, "\u2014"))))))));
}
function productRowProps(history, p) {
  if (!hasTenantFeature('territoryRep') || !p || !p.productId) return {};
  return {
    className: 'clickable',
    style: {
      cursor: 'pointer'
    },
    title: `Open ${p.title || p.productId} product detail`,
    onClick: () => history.push({
      pathname: `/Territory/this_year/product/${encodeURIComponent(p.productId)}`,
      state: p.title ? {
        title: p.title
      } : undefined
    })
  };
}
const pathOf = link => {
  try {
    const u = new URL(String(link));
    return u.pathname === '/' ? u.host : u.pathname;
  } catch (e) {
    return String(link || '');
  }
};

/**
 * Top products. Web views are attributed by product page URL, so products that share one page
 * (a pricing page, a collection) share that page's views and users. Those are grouped under the
 * page instead of repeating the same numbers on every row.
 */
export const TopProductsCard = withRouter(function TopProductsCard({
  rows,
  loading,
  history,
  span = 6,
  hint = 'by views in this window',
  cluster = false
}) {
  const list = rows || [];
  const groups = [];
  const byLink = new Map();
  list.forEach(p => {
    const k = p.link || `#${p.productId}`;
    if (!byLink.has(k)) {
      byLink.set(k, {
        link: p.link,
        items: []
      });
      groups.push(byLink.get(k));
    }
    byLink.get(k).items.push(p);
  });
  const shared = groups.some(g => g.items.length > 1);
  const cols = cluster ? [['Sales qty', p => fmt.int(p.salesQuantity)], ['Conv', p => fmt.int(p.conversions)], ['Conv $', p => fmt.money0(p.conversionsPriceValue)]] : [['Engagements', p => fmt.int(p.engagements)], ['Conv', p => fmt.int(p.conversions)], ['Conv $', p => fmt.money0(p.conversionsPriceValue)]];
  return /*#__PURE__*/React.createElement(Card, {
    title: "Top products",
    hint: shared ? 'products that share one page share its views' : hint,
    span: span
  }, loading ? /*#__PURE__*/React.createElement(Pending, null) : !list.length ? /*#__PURE__*/React.createElement(Empty, null, "No product page views in this footprint for this window. Views arrive as the tracker sees product pages from these markets.") : /*#__PURE__*/React.createElement("div", {
    className: "tblwrap"
  }, /*#__PURE__*/React.createElement("table", {
    className: "territory-table wa-table"
  }, /*#__PURE__*/React.createElement("thead", null, /*#__PURE__*/React.createElement("tr", null, /*#__PURE__*/React.createElement("th", null, "Product"), /*#__PURE__*/React.createElement("th", {
    className: "n"
  }, "Views"), /*#__PURE__*/React.createElement("th", {
    className: "n"
  }, "Users"), cols.map(([h]) => /*#__PURE__*/React.createElement("th", {
    className: "n",
    key: h
  }, h)))), /*#__PURE__*/React.createElement("tbody", null, groups.map(g => {
    if (g.items.length === 1) {
      const p = g.items[0];
      const title = clean(resolveTitle(p.link, p.title) || p.title) || p.productId;
      return /*#__PURE__*/React.createElement("tr", _extends({
        key: p.productId
      }, productRowProps(history, {
        ...p,
        title
      })), /*#__PURE__*/React.createElement("td", null, /*#__PURE__*/React.createElement("span", {
        className: "name"
      }, title)), /*#__PURE__*/React.createElement("td", {
        className: "n"
      }, fmt.int(p.views)), /*#__PURE__*/React.createElement("td", {
        className: "n"
      }, fmt.int(p.uniqueUsers)), cols.map(([h, f]) => /*#__PURE__*/React.createElement("td", {
        className: "n",
        key: h
      }, f(p))));
    }
    const first = g.items[0];
    return /*#__PURE__*/React.createElement(React.Fragment, {
      key: g.link || first.productId
    }, /*#__PURE__*/React.createElement("tr", {
      className: "group"
    }, /*#__PURE__*/React.createElement("td", null, /*#__PURE__*/React.createElement("span", {
      className: "name"
    }, pathOf(g.link)), /*#__PURE__*/React.createElement("span", {
      className: "path"
    }, g.items.length, " products on this page")), /*#__PURE__*/React.createElement("td", {
      className: "n"
    }, fmt.int(first.views)), /*#__PURE__*/React.createElement("td", {
      className: "n"
    }, fmt.int(first.uniqueUsers)), cols.map(([h]) => /*#__PURE__*/React.createElement("td", {
      className: "n dim",
      key: h
    }))), g.items.map(p => {
      const title = clean(resolveTitle(p.link, p.title) || p.title) || p.productId;
      return /*#__PURE__*/React.createElement("tr", _extends({
        key: p.productId
      }, productRowProps(history, {
        ...p,
        title
      })), /*#__PURE__*/React.createElement("td", {
        className: "sub"
      }, title), /*#__PURE__*/React.createElement("td", {
        className: "n dim"
      }, "\xB7"), /*#__PURE__*/React.createElement("td", {
        className: "n dim"
      }, "\xB7"), cols.map(([h, f]) => /*#__PURE__*/React.createElement("td", {
        className: "n",
        key: h
      }, f(p))));
    }));
  })))));
});
export function TopCategoriesCard({
  rows,
  loading,
  span = 6
}) {
  const list = rows || [];
  return /*#__PURE__*/React.createElement(Card, {
    title: "Top categories",
    hint: "rolled up from products in this footprint",
    span: span
  }, loading ? /*#__PURE__*/React.createElement(Pending, null) : !list.length ? /*#__PURE__*/React.createElement(Empty, null, "No categories yet: they roll up from product page views in this footprint.") : /*#__PURE__*/React.createElement("div", {
    className: "tblwrap"
  }, /*#__PURE__*/React.createElement("table", {
    className: "territory-table wa-table"
  }, /*#__PURE__*/React.createElement("thead", null, /*#__PURE__*/React.createElement("tr", null, /*#__PURE__*/React.createElement("th", null, "Category"), /*#__PURE__*/React.createElement("th", {
    className: "n"
  }, "Products"), /*#__PURE__*/React.createElement("th", {
    className: "n"
  }, "Views"), /*#__PURE__*/React.createElement("th", {
    className: "n"
  }, "Sessions"), /*#__PURE__*/React.createElement("th", {
    className: "n"
  }, "Users"), /*#__PURE__*/React.createElement("th", {
    className: "n"
  }, "Conv"), /*#__PURE__*/React.createElement("th", {
    className: "n"
  }, "Conv $"))), /*#__PURE__*/React.createElement("tbody", null, list.map(c => /*#__PURE__*/React.createElement("tr", {
    key: c.category
  }, /*#__PURE__*/React.createElement("td", null, /*#__PURE__*/React.createElement("span", {
    className: "name"
  }, c.category)), /*#__PURE__*/React.createElement("td", {
    className: "n"
  }, fmt.int(c.products)), /*#__PURE__*/React.createElement("td", {
    className: "n"
  }, fmt.int(c.views)), /*#__PURE__*/React.createElement("td", {
    className: "n"
  }, fmt.int(c.sessions)), /*#__PURE__*/React.createElement("td", {
    className: "n"
  }, fmt.int(c.uniqueUsers)), /*#__PURE__*/React.createElement("td", {
    className: "n"
  }, fmt.int(c.conversions)), /*#__PURE__*/React.createElement("td", {
    className: "n"
  }, fmt.money0(c.conversionsPriceValue))))))));
}

/** Ranked zips: footprint zips by default, IP-resolved city matches on the toggle; 50 rows, search. */
export function ZipsCard({
  placeZips,
  cityZips,
  loading,
  marketName,
  span = 6
}) {
  const [mode, setMode] = React.useState('footprint');
  const [q, setQ] = React.useState('');
  const hasCity = (cityZips || []).length > 0;
  const src = mode === 'city' && hasCity ? cityZips : placeZips || [];
  const needle = q.trim().toLowerCase();
  const rows = (needle ? src.filter(z => `${z.postalCode || ''} ${z.city || ''}`.toLowerCase().includes(needle)) : src).slice(0, 50);
  const total = src.reduce((s, z) => s + (Number(z.views) || 0), 0);
  return /*#__PURE__*/React.createElement(Card, {
    title: "Top zips",
    hint: mode === 'city' ? `IP-resolved visitors whose city matched ${marketName}` : `${fmt.int((placeZips || []).length)} zips in the footprint · ${fmt.int(total)} views`,
    span: span,
    right: hasCity ? /*#__PURE__*/React.createElement("span", {
      className: "seg"
    }, /*#__PURE__*/React.createElement("button", {
      className: mode === 'footprint' ? 'on' : '',
      onClick: () => setMode('footprint')
    }, "Footprint"), /*#__PURE__*/React.createElement("button", {
      className: mode === 'city' ? 'on' : '',
      onClick: () => setMode('city')
    }, "City match")) : null
  }, loading ? /*#__PURE__*/React.createElement(Pending, null) : !src.length ? /*#__PURE__*/React.createElement(Empty, null, "No verified visitors from this footprint in this window.") : /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
    className: "wa-toolbar"
  }, /*#__PURE__*/React.createElement(Filters, null, /*#__PURE__*/React.createElement(Search, {
    value: q,
    onChange: setQ,
    placeholder: "Zip or city"
  }))), /*#__PURE__*/React.createElement("div", {
    className: "tblwrap"
  }, /*#__PURE__*/React.createElement("table", {
    className: "territory-table wa-table"
  }, /*#__PURE__*/React.createElement("thead", null, /*#__PURE__*/React.createElement("tr", null, /*#__PURE__*/React.createElement("th", null, "Zip"), /*#__PURE__*/React.createElement("th", null, "City"), /*#__PURE__*/React.createElement("th", {
    className: "n"
  }, "Views"), /*#__PURE__*/React.createElement("th", {
    className: "n"
  }, "Sessions"), /*#__PURE__*/React.createElement("th", {
    className: "n"
  }, "Users"), /*#__PURE__*/React.createElement("th", {
    className: "n"
  }, "Pages/sess"), /*#__PURE__*/React.createElement("th", {
    className: "n"
  }, "Avg sec"), /*#__PURE__*/React.createElement("th", {
    className: "n"
  }, "Conv"))), /*#__PURE__*/React.createElement("tbody", null, rows.map((z, i) => {
    const pps = z.sessions > 0 ? z.views / z.sessions : 0;
    return /*#__PURE__*/React.createElement("tr", {
      key: `${z.postalCode || '—'}-${i}`
    }, /*#__PURE__*/React.createElement("td", null, /*#__PURE__*/React.createElement("b", null, z.postalCode || '—')), /*#__PURE__*/React.createElement("td", {
      className: "dim"
    }, z.city || '—'), /*#__PURE__*/React.createElement("td", {
      className: "n"
    }, fmt.int(z.views)), /*#__PURE__*/React.createElement("td", {
      className: "n"
    }, fmt.int(z.sessions)), /*#__PURE__*/React.createElement("td", {
      className: "n"
    }, fmt.int(z.uniqueUsers)), /*#__PURE__*/React.createElement("td", {
      className: "n"
    }, fmt.dec2(pps)), /*#__PURE__*/React.createElement("td", {
      className: "n"
    }, fmt.dec1(z.avgTimeSpan)), /*#__PURE__*/React.createElement("td", {
      className: "n"
    }, fmt.int(z.conversions)));
  })))), src.length > 50 ? /*#__PURE__*/React.createElement("p", {
    className: "wa-foot"
  }, "Top 50 of ", fmt.int(src.length), " zips. Narrow with search.") : null));
}

/** Deterministic analysis bullets (analysis.js) plus the AI action. */
export function AnalysisCard({
  market,
  marketEntry,
  allMarketData,
  right,
  span = 12
}) {
  const {
    callouts,
    recommendations
  } = deriveAnalysis(market, marketEntry || {}, allMarketData || {});
  const md = text => String(text).split(/(\*\*[^*]+\*\*)/g).map((p, i) => p.startsWith('**') && p.endsWith('**') ? /*#__PURE__*/React.createElement("strong", {
    key: i
  }, p.slice(2, -2)) : /*#__PURE__*/React.createElement(React.Fragment, {
    key: i
  }, p.replace(/ — /g, ': ')));
  return /*#__PURE__*/React.createElement(Card, {
    title: "Analysis",
    hint: "from this window's numbers \xB7 the AI analysis adds demand trends and an executive summary",
    right: right,
    span: span
  }, !callouts.length && !recommendations.length ? /*#__PURE__*/React.createElement(Empty, null, "Nothing to call out yet: this market needs campaign delivery or verified web traffic in the window.") : /*#__PURE__*/React.createElement("ul", {
    className: "wa-insights"
  }, callouts.map((c, i) => /*#__PURE__*/React.createElement("li", {
    key: `c${i}`
  }, md(c))), recommendations.map((r, i) => /*#__PURE__*/React.createElement("li", {
    key: `r${i}`,
    className: "rec"
  }, /*#__PURE__*/React.createElement("b", null, "Recommendation"), " ", md(r)))));
}